import React from "react";
import { Logo } from "./ui";

/* -------------------------------------------------------------------------- */
/* BottomNav — always-visible nav inside the authenticated app.               */
/* -------------------------------------------------------------------------- */

const navItems = [
  { key: "explore", label: "Explorar", icon: ExploreIcon },
  { key: "allies", label: "Aliados", icon: AlliesIcon },
  { key: "activities", label: "Actividades", icon: ActivitiesIcon },
  { key: "requests", label: "Solicitudes", icon: BellIcon },
  { key: "chat", label: "Mensajes", icon: ChatIcon },
  { key: "profile", label: "Perfil", icon: UserIcon },
];

export function BottomNav({ active, onChange, badges = {} }) {
  return (
    <nav
      data-testid="bottom-nav"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center"
    >
      <div
        className="pointer-events-auto w-full max-w-[480px] border-t border-slate-100 bg-white/95 backdrop-blur-xl md:max-w-3xl lg:max-w-5xl md:rounded-b-[36px]"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="grid grid-cols-6 gap-1 px-2 py-2">
        {navItems.map(({ key, label, icon: Icon }) => {
          const isActive = active === key;
          return (
            <button
              key={key}
              onClick={() => onChange(key)}
              data-testid={`nav-${key}`}
              className={`relative flex flex-col items-center justify-center gap-1 rounded-2xl px-2 py-2 text-[11px] font-bold transition ${
                isActive
                  ? "bg-emerald-50 text-emerald-700"
                  : "text-slate-400 hover:bg-slate-50 hover:text-[#0B2D4D]"
              }`}
            >
              <Icon size={22} />
              <span>{label}</span>
              {badges[key] > 0 && (
                <span
                  data-testid={`nav-badge-${key}`}
                  className="absolute right-4 top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#FF5C5C] px-1 text-[10px] font-black text-white"
                >
                  {badges[key] > 99 ? "99+" : badges[key]}
                </span>
              )}
            </button>
          );
        })}
        </div>
      </div>
    </nav>
  );
}

/* -------------------------------------------------------------------------- */
/* Top bar inside auth'ed screens — shows logo + small subtitle.              */
/* -------------------------------------------------------------------------- */

export function TopBar({ city = "", onBackToWeb }) {
  return (
    <header className="sticky top-0 z-20 border-b border-slate-100 bg-white/85 backdrop-blur-xl">
      <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-3">
        <Logo size="md" />
        <div className="flex items-center gap-2">
          <div className="hidden items-center gap-2 rounded-full border border-slate-100 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-500 sm:flex">
            <span aria-hidden="true">📍</span>
            <span>{city || "Sin ubicación"}</span>
          </div>
          {onBackToWeb && (
            <button
              type="button"
              onClick={onBackToWeb}
              data-testid="topbar-back-to-web"
              className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-[#0B2D4D] shadow-sm transition hover:bg-slate-50"
              aria-label="Volver a la web"
            >
              <span aria-hidden="true">←</span> Volver a la web
            </button>
          )}
          <button
            data-testid="notifications-bell"
            className="relative inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-100 bg-white text-[#0B2D4D] transition hover:bg-slate-50"
            aria-label="Notificaciones"
          >
            <BellIcon size={18} />
          </button>
        </div>
      </div>
    </header>
  );
}

/* -------------------------------------------------------------------------- */
/* Simple inline SVG icons to avoid lucide-react                              */
/* -------------------------------------------------------------------------- */

function AlliesIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 21s-6.5-4.35-9-8.5C1.5 9.5 3 6 6 6c1.8 0 3 1 4 2.5C11 7 12.2 6 14 6c3 0 4.5 3.5 3 6.5-2.5 4.15-9 8.5-9 8.5z" />
    </svg>
  );
}

function ActivitiesIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="17" rx="3" />
      <path d="M3 9h18M8 2v4M16 2v4M8 14h.01M12 14h.01M16 14h.01" />
    </svg>
  );
}


function ExploreIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.35-4.35" />
    </svg>
  );
}

function BellIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

function ChatIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function DogIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10 5.172C10 3.78 8.88 2 7.5 2 6 2 5 3.5 5 5.5c0 .8.3 1.5.7 2.1L3 10v5a2 2 0 0 0 2 2h2l2 4h5l1-3h2a2 2 0 0 0 2-2v-4l-3-3 2-3" />
      <path d="M13 7c0-1.1.9-2 2-2" />
    </svg>
  );
}

function UserIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

export { BellIcon, ChatIcon, UserIcon, DogIcon, ExploreIcon };
